import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { favoritesAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Heart, Storefront, Users, Trash, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { stripHtmlToText } from '../utils/text';
import { profileLink } from '../utils/profile';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function FavoritesPage() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await favoritesAPI.list();
      setFavorites(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (favoriteId) => {
    try {
      await favoritesAPI.remove(favoriteId);
      setFavorites(favorites.filter(f => f.favorite_id !== favoriteId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const userFavorites = favorites.filter(f => f.type === 'user');
  const productFavorites = favorites.filter(f => f.type === 'product');

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
          My Favorites
        </h1>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1A1A1A] border border-[#333333] rounded-sm p-1">
            <TabsTrigger 
              value="products" 
              className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-[#999999]"
              data-testid="products-tab"
            >
              <Storefront className="w-4 h-4 mr-2" />
              Products ({productFavorites.length})
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-[#999999]"
              data-testid="users-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              Users ({userFavorites.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {loading ? (
              <div className="text-center py-12 text-[#999999]">Loading...</div>
            ) : productFavorites.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-12 text-center">
                  <Heart className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
                  <p className="text-[#999999] mb-4">No favorite products yet</p>
                  <Link to="/explore">
                    <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">
                      Explore Products
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productFavorites.map((fav) => {
                  const product = fav.target;
                  if (!product) return null;
                  
                  return (
                    <Card 
                      key={fav.favorite_id}
                      className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none overflow-hidden"
                      data-testid={`favorite-product-${fav.favorite_id}`}
                    >
                      {product.image_url && (
                        <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                          <img 
                            src={resolveImageUrl(product.image_url)} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs bg-[#0A0A0A] px-2 py-1 rounded-sm text-[#999999]">
                            {product.category}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(fav.favorite_id)}
                            className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1 h-auto"
                            data-testid={`remove-favorite-${fav.favorite_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <h3 className="font-bold text-white mb-1 line-clamp-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
                          {product.name}
                        </h3>
                        <p className="text-sm text-[#999999] mb-3 line-clamp-2">
                          {stripHtmlToText(product.description)}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <p className="text-xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>
                            {formatCurrency(product.price)}
                          </p>
                          <Link to={`/product/${product.product_id}`}>
                            <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-xs">
                              View
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users">
            {loading ? (
              <div className="text-center py-12 text-[#999999]">Loading...</div>
            ) : userFavorites.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-12 text-center">
                  <Heart className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
                  <p className="text-[#999999] mb-4">No favorite users yet</p>
                  <Link to="/explore">
                    <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">
                      Discover Users
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {userFavorites.map((fav) => {
                  const favUser = fav.target;
                  if (!favUser) return null;
                  
                  return (
                    <Card 
                      key={fav.favorite_id}
                      className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none"
                      data-testid={`favorite-user-${fav.favorite_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3">
                          <Link to={profileLink(favUser)} className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                            {favUser.avatar_url ? (
                              <img 
                                src={favUser.avatar_url} 
                                alt={favUser.name}
                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#22C55E]/10 rounded-full flex items-center justify-center text-[#22C55E] font-bold shrink-0">
                                {favUser.name?.[0]?.toUpperCase() || 'U'}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-bold text-white truncate">{favUser.name}</p>
                              <p className="text-sm text-[#999999] truncate">{favUser.email}</p>
                            </div>
                          </Link>
                          <div className="flex items-center gap-2 shrink-0">
                            <Link to={profileLink(favUser)}>
                              <Button size="sm" className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm hidden sm:flex">
                                View Profile
                              </Button>
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemove(fav.favorite_id)}
                              className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid={`remove-user-favorite-${fav.favorite_id}`}
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
